/**
 * FreeTierMembersTab — Free Tier Members (read-only intelligence).
 *
 *   1. KPI band (6 cards)
 *   2. Members by Activation Position (phase / milestone / idle / enrollment)
 *   3. Member journey drill-down
 *
 * The nurture funnel machinery that used to live here — the Activation Pipeline
 * ribbon and the Nurture Funnel Controls — moved to the Funnel Manager under
 * Marketing Emails on 2026-08-19 (communications-v2/marketing/funnels/). This
 * page answers "where is every Member?"; that one answers "what do we send
 * them?". Keeping both in one 1,000-line component meant free-tier analytics
 * and marketing automation could not be worked on, or reasoned about, apart.
 *
 * ALL data comes from the activation funnel (activation_events + users +
 * chapters via v_member_activation_position). NO Singular.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, MenuItem, Button, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  CircularProgress, Snackbar, Alert, LinearProgress, Pagination,
  Tooltip,
} from '@mui/material';
import { TimelineOutlined as FunnelIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../services/api';
import MemberJourneyDialog from './MemberJourneyDialog';
import MemberLocation from '../common/MemberLocation';
import {
  PHASE_COLOR, INK, SLATE, LINE, TIER_COLOR,
  Phase, MemberPos, phaseLabel, FUNNEL_MANAGER_PATH,
} from '../communications-v2/marketing/funnels/funnelShared';

export default function FreeTierMembersTab() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pipeline, setPipeline] = useState<{ kpis: any; phases: Phase[]; active_funnels: number; total_funnels: number } | null>(null);
  const [members, setMembers] = useState<MemberPos[]>([]);
  const [memberTotal, setMemberTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  // ?user= / ?search= deep links (e.g. from the member drawer) used to be
  // silently ignored — this page took no router params at all.
  const [filters, setFilters] = useState({
    phase: '', tier: '', funnel: '', idle_min: '',
    search: searchParams.get('search') || searchParams.get('user') || '',
  });
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);
  const [journeyDialog, setJourneyDialog] = useState<{ open: boolean; member: MemberPos | null }>({ open: false, member: null });

  const loadPipeline = useCallback(async () => {
    try {
      const pipeRes = await apiService.getActivationPipeline();
      setPipeline((pipeRes as any).data);
    } catch (e: any) {
      setToast({ msg: e?.message || 'Failed to load pipeline', sev: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);


  const loadMembers = useCallback(async () => {
    setMembersLoading(true);
    try {
      const res = await apiService.getActivationPositions({
        page, limit: 25,
        phase: filters.phase || undefined,
        tier: filters.tier || undefined,
        funnel: filters.funnel || undefined,
        idle_min: filters.idle_min ? Number(filters.idle_min) : undefined,
        search: filters.search || undefined,
      });
      const data = (res as any).data;
      setMembers(data?.members || []);
      setMemberTotal(data?.total || 0);
      setTotalPages(data?.total_pages || 1);
    } catch (e: any) {
      setToast({ msg: e?.message || 'Failed to load members', sev: 'error' });
    } finally {
      setMembersLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { loadPipeline(); }, [loadPipeline]);
  useEffect(() => { loadMembers(); }, [loadMembers]);

  const idlePill = (hours: number | null, phase: string) => {
    if (hours == null) return { bg: '#EFEAE0', fg: SLATE, label: '—' };
    const t = pipeline?.phases.find(p => p.phase === phase)?.idle_threshold_hours ?? 24;
    const label = `${hours.toFixed(0)}h idle`;
    if (hours >= t * 2) return { bg: '#FBE3E3', fg: '#7D1935', label };
    if (hours >= t)     return { bg: '#FBEFE2', fg: '#B45309', label };
    return { bg: '#EFEAE0', fg: SLATE, label };
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#1A5F7A' }} /></Box>;
  }

  const k = pipeline?.kpis || {};
  const kpiCards = [
    { lbl: 'Total Free Tier', val: k.total_free_tier ?? 0, color: '#1A5F7A' },
    { lbl: 'Hot Leads', val: k.hot_leads ?? 0, color: '#7D1935' },
    { lbl: 'Idle > 24h', val: k.idle_gt_24h ?? 0, color: '#D4AC0D' },
    { lbl: 'Avg Conv. Score', val: k.avg_conversion_score ?? 0, color: '#D4AC0D' },
    { lbl: 'Premium Ready', val: k.premium_ready ?? 0, color: '#146152' },
    { lbl: 'In Active Nurture', val: k.in_active_nurture ?? 0, color: '#5D6D7E' },
  ];
  const allPaused = (pipeline?.active_funnels ?? 0) === 0;

  return (
    <Box sx={{ pb: 8 }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: 28, color: INK }}>
            Free Tier Conversion Engine
          </Typography>
          <Typography sx={{ color: SLATE, fontSize: 14 }}>
            Where every Member sits in the activation journey — and the nurture funnel pulling them forward.
          </Typography>
        </Box>
        <Chip
          label="Tracking Members since Jun 10, 2026"
          size="small"
          sx={{ bgcolor: '#F9F3E9', border: `1px solid ${LINE}`, color: SLATE, fontWeight: 600, mt: 0.5 }}
        />
      </Box>

      {/* 1. KPI BAND — equal-width flex so all 6 cards are identical size */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
        {kpiCards.map((c) => (
          <Box key={c.lbl} sx={{ flex: '1 1 0', minWidth: 120 }}>
            <Paper sx={{ p: 2, borderRadius: 3, border: `1px solid ${LINE}`, position: 'relative', overflow: 'hidden', height: 96, boxSizing: 'border-box' }}>
              <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: c.color }} />
              <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px', color: SLATE, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.lbl}</Typography>
              <Typography sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: 30, mt: 0.5, lineHeight: 1 }}>{c.val}</Typography>
            </Paper>
          </Box>
        ))}
      </Box>


      {/* 5. MEMBERS BY ACTIVATION POSITION */}
      <Paper sx={{ borderRadius: 3, border: `1px solid ${LINE}`, overflow: 'hidden' }}>
        <Box sx={{ p: 2.5, borderBottom: `1px solid ${LINE}` }}>
          <Typography sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: 19, color: '#1A5F7A' }}>Members by Activation Position</Typography>
          <Typography sx={{ fontSize: 13, color: SLATE }}>Every Member's exact phase, milestone, idle time, and funnel enrollment — in one row.</Typography>
        </Box>
        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 2, borderBottom: `1px solid ${LINE}`, flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Search name or email…" value={filters.search}
            onChange={(e) => { setPage(1); setFilters(f => ({ ...f, search: e.target.value })); }}
            sx={{ flex: 1, minWidth: 220 }} />
          <TextField select size="small" label="Phase" value={filters.phase}
            onChange={(e) => { setPage(1); setFilters(f => ({ ...f, phase: e.target.value })); }} sx={{ minWidth: 120 }}>
            <MenuItem value="">All Phases</MenuItem>
            {['1', '2', '3', '4', '5'].map(p => <MenuItem key={p} value={p}>Phase {p === '5' ? '5–6' : p}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Funnel" value={filters.funnel}
            onChange={(e) => { setPage(1); setFilters(f => ({ ...f, funnel: e.target.value })); }} sx={{ minWidth: 110 }}>
            <MenuItem value="">All Funnels</MenuItem>
            {['F1', 'F2', 'F3', 'F4', 'F5'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Tier" value={filters.tier}
            onChange={(e) => { setPage(1); setFilters(f => ({ ...f, tier: e.target.value })); }} sx={{ minWidth: 100 }}>
            <MenuItem value="">All Tiers</MenuItem>
            {['hot', 'warm', 'cold'].map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Idle >" value={filters.idle_min}
            onChange={(e) => { setPage(1); setFilters(f => ({ ...f, idle_min: e.target.value })); }} sx={{ minWidth: 110 }}>
            <MenuItem value="">Any idle</MenuItem>
            {[24, 48, 72].map(h => <MenuItem key={h} value={String(h)}>{h}h+</MenuItem>)}
          </TextField>
          {(filters.phase || filters.tier || filters.funnel || filters.idle_min || filters.search) && (
            <Button variant="outlined" size="small"
              onClick={() => { setPage(1); setFilters({ phase: '', tier: '', funnel: '', idle_min: '', search: '' }); }}
              sx={{ ml: 1, whiteSpace: 'nowrap', fontSize: 12, textTransform: 'none', borderColor: LINE, color: SLATE }}>
              Clear filters
            </Button>
          )}
          <Typography sx={{ ml: 'auto', fontSize: 13, color: SLATE, fontWeight: 600 }}>{memberTotal} Members</Typography>
        </Box>
        {membersLoading && <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#1A5F7A' } }} />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {['Member', 'Location', 'Activation Position', 'Idle Time', 'Funnel', 'Tier', 'Conv. Score', ''].map((h, i) => (
                  <TableCell key={i} sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px', color: SLATE, fontWeight: 700 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((m) => {
                const color = PHASE_COLOR[m.eligible_funnel_phase] || SLATE;
                const pill = idlePill(m.hours_idle, m.eligible_funnel_phase);
                const tier = m.engagement_tier ? TIER_COLOR[m.engagement_tier] : null;
                return (
                  <TableRow key={m.user_id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{m.name || '—'}</Typography>
                      <Typography sx={{ fontSize: 12.5, color: SLATE }}>{m.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <MemberLocation
                        location_country_code={m.location_country_code}
                        location_region={m.location_region}
                        location_city={m.location_city}
                        location_source={m.location_source}
                        location_confidence={m.location_confidence}
                        location_resolved_at={m.location_resolved_at}
                        compact
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: color }} />
                        <Box>
                          <Typography sx={{ fontSize: 13 }}><b>Phase {m.eligible_funnel_phase === '5' ? '5–6' : m.eligible_funnel_phase}</b></Typography>
                          <Typography sx={{ fontSize: 11.5, color: SLATE }}>MS {m.current_milestone} — {m.milestone_label}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={`⏱ ${pill.label}`} size="small" sx={{ bgcolor: pill.bg, color: pill.fg, fontWeight: 700, fontSize: 11 }} />
                    </TableCell>
                    <TableCell>
                      <Chip label={`${m.eligible_funnel_key} · ${m.enrolled_in_funnel ? 'enrolled' : 'not enrolled'}`} size="small"
                        sx={{ fontSize: 11.5, fontWeight: 600, bgcolor: m.enrolled_in_funnel ? '#E3F1EC' : '#EFEAE0', color: m.enrolled_in_funnel ? '#146152' : SLATE }} />
                    </TableCell>
                    <TableCell>
                      {tier ? <Chip label={m.engagement_tier} size="small" sx={{ bgcolor: tier.bg, color: tier.fg, fontWeight: 700, fontSize: 11, textTransform: 'capitalize' }} />
                        : <Typography sx={{ fontSize: 12, color: SLATE }}>—</Typography>}
                    </TableCell>
                    <TableCell><Typography sx={{ fontWeight: 600 }}>{m.conversion_score ?? '—'}</Typography></TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined"
                        onClick={() => setJourneyDialog({ open: true, member: m })}
                        sx={{ fontSize: 12, color: '#1A5F7A', borderColor: LINE, textTransform: 'none' }}>
                        View journey →
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {members.length === 0 && !membersLoading && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: SLATE }}>No Members match these filters.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
          </Box>
        )}
      </Paper>



      <MemberJourneyDialog
        open={journeyDialog.open}
        member={journeyDialog.member}
        onClose={() => setJourneyDialog({ open: false, member: null })}
      />


      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
